import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, MapPin, Phone, Clock, Instagram } from 'lucide-react';
import { Button } from '../components/ui/button';
import { API } from '../App';
import axios from 'axios';

// External Square booking URL
const BOOKING_URL = "https://book.squareup.com/appointments/ln68161zet4ox2/location/LER0ZJ0A30CK8/services?buttonTextColor=000000&color=e3bc9a&locale=en-AU&referrer=so";

// Assets - Higher quality logo URL
const LOGO = "https://customer-assets.emergentagent.com/job_logan-salon/artifacts/cyvqt31h_IMG_6012.jpg";
const LOGAN_PHOTO = "https://customer-assets-4nw71qhi.emergentagent.net/job_8fe9450f-abea-46bd-98f9-b43f17e0b418/artifacts/be83973d_IMG_8821.jpeg";

// Gallery images with descriptions for mobile
const GALLERY_DATA = [
  // Row 1 - Women
  {
    image: "https://customer-assets.emergentagent.com/job_cdf48f69-7775-45a9-98f1-0b0b20f56037/artifacts/uduqgiso_1FFC36F5-6C5E-4B6A-8272-7F8D14E91A6E.JPG",
    category: "women"
  },
  {
    image: "https://customer-assets.emergentagent.com/job_cdf48f69-7775-45a9-98f1-0b0b20f56037/artifacts/4ktt7y57_4F246893-4E16-4B56-975D-8E3D5177755A.JPG",
    category: "women"
  },
  {
    image: "https://customer-assets.emergentagent.com/job_cdf48f69-7775-45a9-98f1-0b0b20f56037/artifacts/boieobq9_0DC4F3B9-7A68-40B4-AAC4-9EF738954E39.JPG",
    category: "women"
  },
  // Row 2 - Men
  {
    image: "https://customer-assets.emergentagent.com/job_cdf48f69-7775-45a9-98f1-0b0b20f56037/artifacts/6nktuaqp_61BD7F41-7188-415E-892E-4CAD6EF3FB33.JPG",
    category: "men"
  },
  {
    image: "https://customer-assets.emergentagent.com/job_cdf48f69-7775-45a9-98f1-0b0b20f56037/artifacts/wz7158g2_EB2522C4-7785-47B9-9D83-D93EEA0550E6.JPG",
    category: "men"
  },
  {
    image: "https://customer-assets.emergentagent.com/job_cdf48f69-7775-45a9-98f1-0b0b20f56037/artifacts/vwz0ucgz_78C29BD1-2A6B-4342-8A7D-BD289CF77243.JPG",
    category: "men"
  }
];

// Testimonials with updated Jessica T photo
const TESTIMONIALS = [
  {
    name: "Sam",
    text: "I have been a client of Logan's for several years, and my hair has never looked better. I consistently receive compliments, which is a testament to her skill and attention to detail. Logan has an exceptional ability to tailor both cut and colour to suit individual face shape and skin tone. She brings a modern, confident edge to her work that leaves you feeling refreshed and ready to take on the world. Beyond her technical expertise, Logan is warm, personable, and a great listener, making every appointment an enjoyable experience.",
    rating: 5,
    image: "https://customer-assets.emergentagent.com/job_cdf48f69-7775-45a9-98f1-0b0b20f56037/artifacts/tunluorm_1D049F1F-31C8-4373-9DFB-99426660BDD1.JPG"
  },
  {
    name: "Julia Mitchell",
    text: "Logan has been cutting my hair now for a number of years and I have always been very very happy with her work. She takes the time to ensure that she understands what it is that you are looking for in a hairstyle. Logan has a cheerful personality and is very easy to get along with. Her head massages are fabulous. I really enjoy my appointments with her.",
    rating: 5,
    image: "https://customer-assets.emergentagent.com/job_cdf48f69-7775-45a9-98f1-0b0b20f56037/artifacts/lxsv2yc4_94F5B041-B6E4-4A91-99C3-3F170B959AFA.JPG"
  },
  {
    name: "Justin McAllister",
    text: "Logan is a hairdressing genius. She also manages to tame my unruly and bristled beard with aplomb. Good value and highly recommended.",
    rating: 5,
    image: "https://customer-assets.emergentagent.com/job_cdf48f69-7775-45a9-98f1-0b0b20f56037/artifacts/xch61rpy_B6CABF56-FFD0-41D7-BFA4-EBF810A49180.JPG"
  },
  {
    name: "Matt Pocock",
    text: "Logan takes great care getting my haircut the way I like it, she's always bubbly and interested in what you have been up to since your last visit. The hair wash and head massage makes getting your haircut a pleasure and the cost is great value. Thanks Logan.",
    rating: 5,
    image: "https://customer-assets.emergentagent.com/job_cdf48f69-7775-45a9-98f1-0b0b20f56037/artifacts/e9um41dj_9290EF28-1A64-43AE-BB46-AFFD3F54CF18.JPG"
  }
];

// Scroll animation hook
const useScrollAnimation = () => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
};

// Animated section component
const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const [ref, isVisible] = useScrollAnimation();
  
  return (
    <div
      ref={ref}
      className={`transform transition-all duration-700 ease-out ${className}`}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
        transitionDelay: `${delay}ms`
      }}
    >
      {children}
    </div>
  );
};

export default function LandingPage({ discountClaimed, onGiftClick }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [heroVisible, setHeroVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    fetchServices();
    setTimeout(() => setHeroVisible(true), 100);
    
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    } finally {
      setLoading(false);
    }
  };

  // Group services by category
  const groupedServices = services.reduce((acc, service) => {
    if (!acc[service.category]) {
      acc[service.category] = [];
    }
    acc[service.category].push(service);
    return acc;
  }, {});

  const scrollToSection = (id) => {
    setMobileMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-cream pb-16 md:pb-0">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 nav-glass border-b border-[#D6CFC2]/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo - Text only */}
            <Link to="/" className="logo-container flex items-center" data-testid="nav-logo">
              <span className="font-heading text-base sm:text-lg md:text-xl text-bronze tracking-wide">Logan Collective</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <button onClick={() => scrollToSection('services')} className="text-[#1A1A1A] hover:text-bronze transition-colors text-sm tracking-wide" data-testid="nav-services">Services</button>
              <button onClick={() => scrollToSection('gallery')} className="text-[#1A1A1A] hover:text-bronze transition-colors text-sm tracking-wide" data-testid="nav-gallery">Gallery</button>
              <button onClick={() => scrollToSection('about')} className="text-[#1A1A1A] hover:text-bronze transition-colors text-sm tracking-wide" data-testid="nav-about">About</button>
              <button onClick={() => scrollToSection('contact')} className="text-[#1A1A1A] hover:text-bronze transition-colors text-sm tracking-wide" data-testid="nav-contact">Contact</button>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-bronze text-[#1A1A1A] hover:bg-bronze-light transition-colors duration-300 px-6 py-2 text-sm tracking-widest uppercase font-semibold" data-testid="nav-book-btn">
                  Book Now
                </Button>
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} className="text-[#1A1A1A]" /> : <Menu size={24} className="text-[#1A1A1A]" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-cream border-t border-[#D6CFC2] animate-fade-in">
            <div className="px-6 py-6 space-y-4">
              <button onClick={() => scrollToSection('services')} className="block w-full text-left text-[#1A1A1A] hover:text-bronze py-2 text-lg">Services</button>
              <button onClick={() => scrollToSection('gallery')} className="block w-full text-left text-[#1A1A1A] hover:text-bronze py-2 text-lg">Gallery</button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left text-[#1A1A1A] hover:text-bronze py-2 text-lg">About</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left text-[#1A1A1A] hover:text-bronze py-2 text-lg">Contact</button>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="block">
                <Button className="w-full bg-[#1A1A1A] text-cream hover:bg-bronze transition-colors duration-300 py-4 text-sm tracking-widest uppercase font-semibold" data-testid="mobile-book-btn">
                  Book Now
                </Button>
              </a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section - Creative Minimal Layout */}
      <section className="min-h-screen pt-16 sm:pt-20 bg-cream relative overflow-hidden" data-testid="hero-section">
        {/* Background decorative elements */}
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-bronze/5 to-transparent" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-bronze/5 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 w-full h-full relative z-10">
          <div className="min-h-[calc(100vh-5rem)] flex flex-col justify-center py-12 md:py-0">
            
            {/* Centered Content */}
            <div className="max-w-3xl mx-auto text-center">
              <div 
                className={`transition-all duration-700 ease-out ${
                  heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
                style={{ transitionDelay: '100ms' }}
              >
                {/* Logo centered */}
                <div className="w-36 h-36 md:w-44 md:h-44 mx-auto mb-6 rounded-full overflow-hidden border-2 border-bronze/30 shadow-lg">
                  <img src={LOGO} alt="Logan Collective" className="w-full h-full object-cover" />
                </div>
                
                {/* Decorative line + subtitle */}
                <div className="flex items-center justify-center gap-4 mb-6">
                  <div className="w-8 h-px bg-bronze"></div>
                  <p className="text-xs tracking-[0.35em] uppercase text-bronze">
                    Hairdresser & Barber
                  </p>
                  <div className="w-8 h-px bg-bronze"></div>
                </div>
              </div>
              
              <h1 
                className={`font-heading text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-medium text-[#1A1A1A] leading-[1.05] mb-6 transition-all duration-700 ease-out ${
                  heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: '200ms' }}
              >
                More Than<br />
                <span className="text-bronze italic">A Haircut</span>
              </h1>
              
              <p 
                className={`text-sm sm:text-base md:text-lg text-[#1A1A1A]/60 leading-relaxed mb-10 max-w-lg mx-auto transition-all duration-700 ease-out ${
                  heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: '300ms' }}
              >
                Experience the art of hair at Logan Collective. With over 7 years of expertise, 
                every cut tells your story.
              </p>
              
              {/* Buttons */}
              <div 
                className={`flex flex-col sm:flex-row gap-3 justify-center transition-all duration-700 ease-out ${
                  heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: '400ms' }}
              >
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
                  <Button className="w-full sm:w-auto group bg-bronze text-[#1A1A1A] hover:bg-bronze-light transition-all duration-500 px-10 py-4 text-xs tracking-[0.2em] uppercase font-semibold rounded-full" data-testid="hero-book-btn">
                    <span className="flex items-center justify-center gap-3">
                      Book Appointment
                      <span className="group-hover:translate-x-1 transition-transform duration-300">→</span>
                    </span>
                  </Button>
                </a>
                <Button 
                  className="w-full sm:w-auto group border border-[#1A1A1A]/20 bg-transparent text-[#1A1A1A] hover:border-bronze hover:bg-bronze hover:text-[#1A1A1A] transition-all duration-500 px-10 py-4 text-xs tracking-[0.2em] uppercase font-semibold rounded-full"
                  onClick={() => scrollToSection('services')}
                  data-testid="hero-services-btn"
                >
                  <span className="flex items-center justify-center gap-3">
                    View Services
                    <span className="group-hover:translate-y-0.5 transition-transform duration-300">↓</span>
                  </span>
                </Button>
              </div>
              
              {/* Stats row */}
              <div 
                className={`flex items-center justify-center gap-8 md:gap-12 mt-12 md:mt-16 transition-all duration-700 ease-out ${
                  heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: '500ms' }}
              >
                <div className="text-center">
                  <p className="font-heading text-3xl md:text-4xl text-bronze">7+</p>
                  <p className="text-[10px] md:text-xs text-[#1A1A1A]/40 uppercase tracking-wider mt-1">Years</p>
                </div>
                <div className="w-px h-10 bg-[#D6CFC2]"></div>
                <div className="text-center">
                  <p className="font-heading text-3xl md:text-4xl text-bronze">500+</p>
                  <p className="text-[10px] md:text-xs text-[#1A1A1A]/40 uppercase tracking-wider mt-1">Clients</p>
                </div>
                <div className="w-px h-10 bg-[#D6CFC2]"></div>
                <div className="text-center">
                  <p className="font-heading text-3xl md:text-4xl text-bronze">25</p>
                  <p className="text-[10px] md:text-xs text-[#1A1A1A]/40 uppercase tracking-wider mt-1">Services</p>
                </div>
              </div>
            </div>
            
            {/* Location badge - bottom */}
            <div 
              className={`absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex items-center gap-2 text-[#1A1A1A]/40 transition-all duration-700 ease-out ${
                heroVisible ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ transitionDelay: '600ms' }}
            >
              <MapPin size={14} />
              <span className="text-xs tracking-wider">Albany, Western Australia</span>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 sm:py-20 md:py-32 bg-cream" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-10 sm:gap-16 items-center">
            <AnimatedSection className="order-2 lg:order-1">
              <img 
                src={LOGAN_PHOTO} 
                alt="Logan" 
                className="w-full aspect-[4/5] object-cover"
              />
            </AnimatedSection>
            <AnimatedSection className="order-1 lg:order-2" delay={200}>
              <p className="text-sm tracking-[0.3em] uppercase text-bronze mb-4">About Logan</p>
              <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl font-medium text-[#1A1A1A] mb-5 sm:mb-8">Meet Your Stylist</h2>
              <div className="space-y-4 sm:space-y-6 text-[#1A1A1A]/70 leading-relaxed text-sm sm:text-base">
                <p>
                  Hey there! I'm Logan, a senior stylist and barber with over 7 years of experience 
                  in the industry. I'm passionate about helping you look and feel your absolute best.
                </p>
                <p>
                  Working out of Kove The Studio in Albany, I specialise in everything from classic 
                  cuts to modern fades, beautiful balayages to bright blondes. Every head of hair 
                  is unique, and I love the creative challenge of finding the perfect style for you.
                </p>
                <p>
                  At Logan Collective, it's not just about the hair, it's about the experience. 
                  Book in and let's create something beautiful together.
                </p>
              </div>
              <div className="mt-6 sm:mt-8 flex items-center gap-4">
                <a 
                  href="https://instagram.com/logan.collective" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-bronze hover:text-[#1A1A1A] transition-colors"
                  data-testid="instagram-link"
                >
                  <Instagram size={20} />
                  <span className="text-sm sm:text-base">@logan.collective</span>
                </a>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-16 sm:py-20 md:py-32 bg-cream-dark" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <AnimatedSection className="text-center mb-10 sm:mb-16">
            <p className="text-sm tracking-[0.3em] uppercase text-bronze mb-4">Our Services</p>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl font-medium text-[#1A1A1A]">Service Menu</h2>
          </AnimatedSection>

          {loading ? (
            <div className="flex justify-center">
              <div className="spinner"></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-10 sm:gap-12 md:gap-20">
              {Object.entries(groupedServices).map(([category, categoryServices], categoryIndex) => (
                <AnimatedSection key={category} delay={categoryIndex * 100}>
                  <h3 className="font-heading text-xl sm:text-2xl md:text-3xl text-[#1A1A1A] mb-6 sm:mb-10 pb-3 sm:pb-4 border-b-2 border-bronze/30">
                    {category}
                  </h3>
                  <div className="space-y-5 sm:space-y-8">
                    {categoryServices.map((service) => (
                      <div key={service.id} className="group" data-testid={`service-${service.id}`}>
                        <div className="flex items-baseline">
                          <div className="flex-shrink-0 pr-2">
                            <h4 className="text-[#1A1A1A] font-medium text-sm sm:text-base md:text-lg group-hover:text-bronze transition-colors">{service.name}</h4>
                          </div>
                          <div className="flex-1 border-b border-dotted border-[#1A1A1A]/20 mx-2 mb-1"></div>
                          <span className="font-heading text-lg sm:text-xl md:text-2xl text-bronze flex-shrink-0">${service.price}</span>
                        </div>
                        {service.description && (
                          <p className="text-xs sm:text-sm text-[#1A1A1A]/50 mt-1 leading-relaxed">{service.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </AnimatedSection>
              ))}
            </div>
          )}

          <AnimatedSection className="text-center mt-10 sm:mt-16" delay={400}>
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
              <Button className="w-full sm:w-auto group relative overflow-hidden bg-bronze text-cream hover:bg-bronze-light transition-all duration-500 px-10 sm:px-14 py-5 sm:py-7 text-sm tracking-widest uppercase font-bold shadow-lg hover:shadow-xl hover:scale-[1.02]" data-testid="services-book-btn">
                <span className="relative z-10 flex items-center justify-center gap-2">
                  Book Your Service
                  <span className="group-hover:translate-x-1 transition-transform duration-300">→</span>
                </span>
              </Button>
            </a>
          </AnimatedSection>
        </div>
      </section>

      {/* Gallery Section - Mobile has descriptions */}
      <section id="gallery" className="py-16 sm:py-20 md:py-32 bg-cream" data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <AnimatedSection className="text-center mb-10 sm:mb-16">
            <p className="text-sm tracking-[0.3em] uppercase text-bronze mb-4">Our Work</p>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl font-medium text-[#1A1A1A]">Gallery</h2>
          </AnimatedSection>

          {/* Mobile Gallery - 2 column grid, clean and simple */}
          <div className="md:hidden">
            <div className="grid grid-cols-2 gap-3">
              {GALLERY_DATA.map((item, index) => (
                <AnimatedSection key={index} delay={index * 100}>
                  <div className="gallery-item aspect-square overflow-hidden rounded-lg shadow-sm" data-testid={`gallery-item-${index}`}>
                    <img 
                      src={item.image} 
                      alt={`Gallery ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>

          {/* Desktop Gallery - 3x2 grid with elegant spacing */}
          <div className="hidden md:block">
            <div className="grid grid-cols-3 gap-6">
              {GALLERY_DATA.map((item, index) => (
                <AnimatedSection key={index} delay={index * 100}>
                  <div className="gallery-item aspect-[3/4] overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300" data-testid={`gallery-item-desktop-${index}`}>
                    <img 
                      src={item.image} 
                      alt={`Gallery ${index + 1}`}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 sm:py-20 md:py-32 bg-cream-dark" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <AnimatedSection className="text-center mb-10 sm:mb-16">
            <p className="text-sm tracking-[0.3em] uppercase text-bronze mb-4">Testimonials</p>
            <h2 className="font-heading text-2xl sm:text-3xl md:text-5xl font-medium text-[#1A1A1A]">Client Love</h2>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {TESTIMONIALS.map((testimonial, index) => (
              <AnimatedSection key={index} delay={index * 100}>
                <div className="testimonial-card bg-cream p-6 sm:p-8 border border-[#D6CFC2] h-full rounded-lg" data-testid={`testimonial-${index}`}>
                  {/* Stars */}
                  <div className="flex text-bronze text-lg mb-4">
                    {[...Array(5)].map((_, i) => (
                      <span key={i}>★</span>
                    ))}
                  </div>
                  
                  {/* Quote */}
                  <p className="text-[#1A1A1A]/70 leading-relaxed text-sm sm:text-base mb-6">"{testimonial.text}"</p>
                  
                  {/* Author */}
                  <div className="flex items-center gap-3 pt-4 border-t border-[#D6CFC2]">
                    <div className="w-10 h-10 rounded-full bg-bronze/20 flex items-center justify-center">
                      <span className="font-heading text-bronze text-lg">{testimonial.name.charAt(0)}</span>
                    </div>
                    <div>
                      <h4 className="font-heading text-base text-[#1A1A1A]">{testimonial.name}</h4>
                      <p className="text-xs text-[#1A1A1A]/40">Verified Client</p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
          
          {/* Google Reviews Link */}
          <AnimatedSection className="text-center mt-10" delay={400}>
            <p className="text-[#1A1A1A]/50 text-sm">
              Read more reviews on{' '}
              <a href="https://www.google.com/maps/place/Logan+Collective/@-35.0202465,117.8815854,17z/data=!4m8!3m7!1s0x2a3927dfc8201de7:0x7946bb5962ed0571!8m2!3d-35.0202465!4d117.8841603!9m1!1b1!16s%2Fg%2F11xg56g67f?entry=ttu&g_ep=EgoyMDI2MDMwNC4xIKXMDSoASAFQAw%3D%3D" target="_blank" rel="noopener noreferrer" className="text-bronze hover:underline">
                Google
              </a>
            </p>
          </AnimatedSection>
        </div>
      </section>


      {/* Contact & Hours Section */}
      <section id="contact" className="py-16 sm:py-20 md:py-32 bg-[#1A1A1A]" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="grid md:grid-cols-3 gap-8 sm:gap-10 md:gap-8">
            <AnimatedSection>
              <div className="text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start gap-3 mb-4 sm:mb-6">
                  <MapPin className="text-bronze" size={22} />
                  <h3 className="font-heading text-lg sm:text-2xl text-cream">Find Us</h3>
                </div>
                <p className="text-cream/70 leading-relaxed text-sm sm:text-base">
                  364 Middleton Loop<br />
                  Albany, Western Australia
                </p>
                <p className="text-cream/50 text-xs sm:text-sm mt-3 sm:mt-4">Inside Kove The Studio</p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={100}>
              <div className="text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start gap-3 mb-4 sm:mb-6">
                  <Clock className="text-bronze" size={22} />
                  <h3 className="font-heading text-lg sm:text-2xl text-cream">Opening Hours</h3>
                </div>
                <div className="space-y-1.5 sm:space-y-2 text-cream/70 text-sm">
                  <p><span className="text-cream/50">Sun - Tue:</span> Closed</p>
                  <p><span className="text-cream/50">Wednesday:</span> 10am - 8pm</p>
                  <p><span className="text-cream/50">Thursday:</span> 9am - 6pm</p>
                  <p><span className="text-cream/50">Friday:</span> 9am - 4pm</p>
                  <p><span className="text-cream/50">Saturday:</span> Closed</p>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <div className="text-center md:text-left">
                <div className="flex items-center justify-center md:justify-start gap-3 mb-4 sm:mb-6">
                  <Phone className="text-bronze" size={22} />
                  <h3 className="font-heading text-lg sm:text-2xl text-cream">Contact</h3>
                </div>
                <a 
                  href="tel:0438814493" 
                  className="text-cream/70 hover:text-bronze transition-colors text-lg sm:text-xl block mb-3 sm:mb-4"
                  data-testid="phone-link"
                >
                  0438 814 493
                </a>
                <p className="text-cream/50 text-xs sm:text-sm mb-4 sm:mb-6">Call or text to enquire</p>
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                  <Button className="group relative overflow-hidden bg-bronze text-cream hover:bg-bronze-light transition-all duration-500 px-8 sm:px-10 py-4 sm:py-5 text-sm tracking-widest uppercase font-bold shadow-lg hover:shadow-xl hover:scale-[1.02]" data-testid="contact-book-btn">
                    <span className="relative z-10 flex items-center justify-center gap-2">
                      Book Online
                      <span className="group-hover:translate-x-1 transition-transform duration-300">→</span>
                    </span>
                  </Button>
                </a>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 sm:py-8 bg-[#1A1A1A] border-t border-cream/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full overflow-hidden">
              <img src={LOGO} alt="Logan Collective" className="w-full h-full object-cover" />
            </div>
            <span className="text-cream/70 text-xs sm:text-sm">Logan Collective © 2024</span>
            <a 
              href="https://instagram.com/logan.collective" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-cream/50 hover:text-bronze transition-colors"
            >
              <Instagram size={20} />
            </a>
          </div>
          <a 
            href="https://layerly.info" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-cream/50 hover:text-bronze transition-colors text-xs sm:text-sm"
          >
            Made by Layerly
          </a>
        </div>
      </footer>

      {/* Mobile Sticky Book Now CTA */}
      <div className="md:hidden fixed bottom-4 left-4 right-4 z-40">
        <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="block">
          <Button 
            className="w-full group relative overflow-hidden bg-bronze text-cream hover:bg-bronze-light transition-all duration-300 py-4 text-sm tracking-widest uppercase font-bold shadow-xl rounded-full"
            data-testid="sticky-book-btn"
          >
            <span className="relative z-10 flex items-center justify-center gap-2">
              Book Now
              <span className="group-hover:translate-x-1 transition-transform duration-300">→</span>
            </span>
          </Button>
        </a>
      </div>
    </div>
  );
}
